import { useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "components/DataTable/DataTable";
import Pod from "components/Pod/Pod";
import { Button } from "components/Button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import "./ContentCreationCourses.scss"
import { DataFetcher } from "../../../components/DataFetcher";
import { getFetchOptions } from "utils/fetch";

const STATUS_MAP = {
	0: "Unpublished Changes",
	1: "Published",
	2: "Hidden",
};

// DataFetcher request contexts.
const RequestContext = Object.freeze({
	ListCourses: "ListCourses",
	CreateDraft: "CreateDraft",
});

const ContentCreationCourses = () => {
	const [searchValue, setSearchValue] = useState("");
	const [fetchData, setFetchData] = useState({ url: "/api/v3/Courses", context: RequestContext.ListCourses });
	const [data, setData] = useState(null);
	const navigate = useNavigate();
	const tempCourseIdRef = useRef(-1);

	const handleData = (fetchedData, token, context) => {
		if (!fetchedData) {
			return;
		}
		switch (context) {
			case RequestContext.CreateDraft:
				navigate(`/editcourse#draftId-${fetchedData.DraftId}`);
				break;
			case RequestContext.ListCourses: {
				const existingNegativeIds = fetchedData
					.filter((c) => c.Id < 0)
					.map((c) => c.Id);
				if (existingNegativeIds.length > 0) {
					tempCourseIdRef.current = Math.min(...existingNegativeIds) - 1;
				}
				setData(fetchedData);
				break;
			}
			default:
				break;
		}
	};

	const handleAddCourse = () => {
		const newCourseId = tempCourseIdRef.current;
		tempCourseIdRef.current -= 1; // Decrement for next temporary ID
		const options = getFetchOptions({
			method: "PUT",
			body: {
				Id: newCourseId,
				Name: "",
				Description: "",
				ImageUrl: "",
				Modules: [],
				PublishedCourse: {}
			}
		});
		setFetchData({ url: "/api/CourseDraft", options, context: RequestContext.CreateDraft });
	};

	const filtered = useMemo(() => {
		if (!data) {
			return [];
		}
		const query = searchValue.trim().toLocaleLowerCase();
		if (!query) {
			return data;
		}
		return data.filter((course) =>
			course.Name.toLowerCase().includes(query) ||
			(course.Offers?.some(offer => offer.toLowerCase().includes(query))) ||
			String(course.UserCount).includes(query) ||
			STATUS_MAP[course.CourseStatus]?.toLowerCase().includes(query)
		);
	}, [data, searchValue]);

	const statusTemplate = (row) => STATUS_MAP[row.CourseStatus] ?? "-";
	const usersTemplate = (row) => row.UserCount?.toLocaleString?.() ?? row.UserCount;
	const editTemplate = (row) => (
		<Button
			text="Edit"
			to={row.Id < 0
				? `/editcourse#draftId-${row.DraftId}`
				: `/editcourse#courseId-${row.Id}`}
		/>
	);

	const offersTemplate = (row) => {
		const offers = row.Offers;
		if (!offers || offers.length === 0) {
			return <em>(No offers assigned)</em>;
		}
		return <span className="offers-cell">{offers.join(", ")}</span>;
	};

	const columns = [
		{
			field: "Name",
			headerName: "Course Name", valueGetter: (row) => row.Name,
			sortable: true,
			style: { maxWidth: "200px", whiteSpace: "normal", wordBreak: "break-word" }
		},
		{
			field: "Offers",
			headerName: "Offers",
			valueGetter: offersTemplate,
			sortable: true,
			style: { maxWidth: "300px", whiteSpace: "normal", wordBreak: "break-word" }
		},
		{ field: "CourseStatus", headerName: "Status", valueGetter: statusTemplate, sortable: true },
		{ field: "UserCount", headerName: "Users Enrolled", valueGetter: usersTemplate, sortable: true },
		{ headerName: "Actions", valueGetter: editTemplate, sortable: false, removeHeaderFromMobile: true },
	];

	return (
		<DataFetcher fetchData={fetchData} setData={handleData}>
			<div className="content-creation-courses gutters">
				<Pod title="All Courses">
					<div className="datatable-pod">
						<div className="datatable-pod-buttons">
							<div className="searchbox">
								<input
									className="searchbox-input"
									type="text"
									placeholder="Search courses"
									value={searchValue}
									onChange={(e) => setSearchValue(e.target.value)}
								/>
								<span className="searchbox-icon">
									<FontAwesomeIcon icon={faMagnifyingGlass} />
								</span>
							</div>
							<Button text="Add Course" onClick={handleAddCourse} />
						</div>

						<DataTable
							rows={filtered}
							columns={columns}
							rowsPerPage={10}
							useCardView={true}
							noSearchOnMobile={true}
						/>

						<div className="datatable-pod-buttons">
							<Button text="Add Course" onClick={handleAddCourse} />
						</div>
					</div>
				</Pod>
			</div>
		</DataFetcher>
	);
};

export default ContentCreationCourses;
